export type EditableTag = {
  id: string;
  dimensionKey: string;
  name: string;
  value: unknown;
  allowedValues: string[] | null;
  valueType?: string | null;
  /** The tag holds a list of values rather than one. */
  multi?: boolean;
  labelTooltip?: string | null;
  valueTooltip?: string | null;
};

export type TagValueControlKind = "multi-select" | "select" | "input" | "chips";

/**
 * A single-value control (TextInput/Select) can't represent several discrete
 * values, so an array never reaches here in practice — `tagValueControlKind`
 * routes it to "chips" first. Blank rather than split/join is the safe
 * fallback for a caller that skips that routing.
 */
export function tagInputValue(value: unknown): string {
  if (value == null || Array.isArray(value)) return "";
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }
  return "";
}

export function tagChipValues(value: unknown): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value.flatMap((item) => tagChipValues(item));
  }
  if (typeof value === "object") {
    return [JSON.stringify(value)];
  }
  const text = String(value).trim();
  return text ? [text] : [];
}

export function tagValueControlKind(
  tag: Pick<EditableTag, "allowedValues" | "valueType" | "value" | "multi">,
): TagValueControlKind {
  if (tag.multi && tag.allowedValues != null && tag.allowedValues.length > 0) {
    return "multi-select";
  }
  // Several discrete values: show them as chips rather than picking one to
  // show in a single select or input, which would silently drop the rest.
  if (Array.isArray(tag.value)) return "chips";
  if (tag.allowedValues != null && tag.allowedValues.length > 0) {
    return "select";
  }
  if (
    tag.valueType === "text" ||
    tag.valueType === "number" ||
    tag.valueType === "enum"
  ) {
    return "input";
  }
  return "chips";
}

/**
 * Select options are the definition list, plus the stored value when it is
 * not in that list, so the field shows what is actually saved.
 */
export function tagSelectOptions(
  allowedValues: readonly string[],
  current: string,
): string[] {
  const values = [...allowedValues];
  if (current && !values.includes(current)) {
    values.unshift(current);
  }
  return values;
}

/**
 * Multi-select options are the definition list, plus any stored value that
 * is not in that list, so the field shows everything that is actually saved.
 */
export function tagMultiSelectOptions(
  allowedValues: readonly string[],
  current: readonly string[],
): string[] {
  const listed = new Set(allowedValues);
  return [...current.filter((value) => !listed.has(value)), ...allowedValues];
}

export function sameTagValues(
  left: readonly string[],
  right: readonly string[],
): boolean {
  const leftValues = new Set(left);
  const rightValues = new Set(right);
  return (
    leftValues.size === rightValues.size &&
    [...leftValues].every((value) => rightValues.has(value))
  );
}

export function applyTagValue<
  Row extends { dimensionKey: string; value: unknown },
>(
  rows: ReadonlyArray<Row>,
  match: (row: Row) => boolean,
  patch: Partial<Row> & { value: string },
): Row[] {
  return rows.map((row) => (match(row) ? { ...row, ...patch } : row));
}
